import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, AlertTriangle, ChevronDown, Quote, Clock, ChevronRight, User, DollarSign, Timer, RotateCcw, Gauge, ShieldAlert, GitBranch, Flame } from "lucide-react";
import { cn } from "@/lib/utils";
import { StrategicOption } from "./StrategicOptionCard";
import { archetypeConfig } from "@/lib/archetypeConfig";
import { calculateAlignmentScore, calculateEffectiveExposure } from "@/lib/utils/alignmentScore";

interface StrategicOptionCardCompactProps {
  option: StrategicOption;
  optionLabel: string;
  sessionMeta?: Record<string, any>;
  className?: string;
  style?: React.CSSProperties;
}

// Neutrale Fallbacks pro Archetyp – nur wenn kein kontextspezifischer differentiation_factor vorhanden
const archetypeFallback: Record<string, string> = {
  low_risk: "Klarheit über den Markt – aber kein Marktanteil.",
  value_max: "Reichweite ohne Validierung der Kernhypothese.",
  scaling: "Architektur vor Marktbeweis."
};

export const StrategicOptionCardCompact = ({ option, optionLabel, sessionMeta, className, style }: StrategicOptionCardCompactProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMoreBullets, setShowMoreBullets] = useState(false);
  const config = archetypeConfig[option.archetype] || archetypeConfig.low_risk;
  const Icon = config.icon;
  const isScaling = option.archetype === "scaling";
  const truth = option.differentiation_factor || archetypeFallback[option.archetype] || "";
  const hasEconomicMetrics = option.estimated_cost_min != null || option.capacity_requirement_percent != null;

  // Constraint flags
  const isConstraintBreak = option.is_constraint_break === true;
  const isStrategyShift = option.is_strategy_shift === true;
  const hasConstraintWarning = isConstraintBreak || isStrategyShift;

  // Capacity calculation: baseline + option
  const phase1_2 = sessionMeta?.phase1_2_conflict || {};
  const phase1_3 = sessionMeta?.phase1_3_irreversibility || {};
  const baselineCapacity = phase1_2.capacity_percent;
  const totalCapacity = baselineCapacity != null && option.capacity_requirement_percent != null
    ? baselineCapacity + option.capacity_requirement_percent
    : null;

  // Effective exposure
  const validationDeadlineDays = phase1_3.validation_deadline_days;
  const dailyExposure = phase1_3.daily_exposure 
    || (phase1_3.exposure_until_validation && validationDeadlineDays 
        ? Math.round(phase1_3.exposure_until_validation / validationDeadlineDays) 
        : undefined);
  const effectiveExposure = dailyExposure != null
    ? calculateEffectiveExposure(Number(dailyExposure), option.inaction_cost_effect)
    : null;

  // Alignment score
  const commitMode = sessionMeta?.phase2_commit_mode === true;
  const alignment = calculateAlignmentScore({
    is_constraint_break: isConstraintBreak,
    is_strategy_shift: isStrategyShift,
    total_capacity_percent: totalCapacity ?? undefined,
    reversibility_level: option.reversibility_level,
    inaction_cost_effect: option.inaction_cost_effect,
    commit_mode: commitMode,
    time_to_first_signal_days: option.time_to_first_signal_days,
    validation_deadline_days: phase1_3.validation_deadline_days,
  });

  const formatEur = (min?: number, max?: number) => {
    if (min == null && max == null) return null;
    const fmt = (v: number) => v >= 1000 ? `${Math.round(v / 1000)}k` : v.toLocaleString("de-DE");
    if (min != null && max != null) return `${fmt(min)}–${fmt(max)} €`;
    if (min != null) return `ab ${fmt(min)} €`;
    return `bis ${fmt(max!)} €`;
  };

  const reversibilityBadge = (level?: string) => {
    if (level === "hoch") return { label: "Hoch", className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" };
    if (level === "mittel") return { label: "Mittel", className: "border-border bg-muted/50 text-muted-foreground" };
    if (level === "gering") return { label: "Gering", className: "border-destructive/30 bg-destructive/10 text-destructive" };
    return null;
  };

  // Get first advantage, risk, and strategic goal as quick bullets
  const quickBullets = [
    { type: 'goal', text: option.strategic_goal },
    { type: 'plus', text: option.key_advantages[0] },
    { type: 'minus', text: option.key_risks[0] }
  ].filter(b => b.text);

  // Additional advantages and risks (beyond the first one)
  const additionalAdvantages = option.key_advantages.slice(1);
  const additionalRisks = option.key_risks.slice(1);
  const hasMoreBullets = additionalAdvantages.length > 0 || additionalRisks.length > 0;

  return (
    <Card 
      className={cn(
        "relative overflow-hidden border-l-4 transition-all duration-300",
        config.headerColor,
        isOpen && "ring-2 ring-primary/30 shadow-lg shadow-primary/5 scale-[1.01]",
        !isOpen && "hover:shadow-md hover:border-l-[6px]",
        // Option C (scaling) visuell abgesetzt
        isScaling && "opacity-80 border-dashed",
        // Constraint warnings: dashed border + lower opacity
        hasConstraintWarning && !isScaling && "opacity-85 border-dashed",
        className
      )} 
      style={style}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className={cn(
            "w-full text-left p-5 transition-colors",
            !isOpen && "hover:bg-muted/30"
          )}>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3 flex-1 min-w-0">
                {/* Option Label + Archetype + Alignment */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold text-foreground">{optionLabel}</span>
                  <Badge 
                    variant="outline" 
                    className={cn("px-2 py-0.5 text-xs font-medium border gap-1", config.color)}
                  >
                    <Icon className="h-3 w-3" />
                    <span>{config.label}</span>
                  </Badge>
                  {/* Alignment Score Badge */}
                  <Badge variant="outline" className={cn("px-2 py-0.5 text-xs font-semibold border gap-1", alignment.color,
                    alignment.score >= 80 ? "border-emerald-500/30 bg-emerald-500/10" :
                    alignment.score >= 50 ? "border-amber-500/30 bg-amber-500/10" :
                    "border-destructive/30 bg-destructive/10"
                  )}>
                    {alignment.score}/100
                  </Badge>
                  {/* Constraint Break Warning */}
                  {isConstraintBreak && (
                    <Badge variant="outline" className="px-2 py-0.5 text-xs border-destructive/30 bg-destructive/10 text-destructive gap-1">
                      <ShieldAlert className="h-3 w-3" />
                      Deadline-Bruch
                    </Badge>
                  )}
                  {/* Strategy Shift Warning */}
                  {isStrategyShift && (
                    <Badge variant="outline" className="px-2 py-0.5 text-xs border-destructive/30 bg-destructive/10 text-destructive gap-1">
                      <GitBranch className="h-3 w-3" />
                      Strategiewechsel
                    </Badge>
                  )}
                  {/* Scaling-Warnung */}
                  {isScaling && (
                    <Badge variant="outline" className="px-2 py-0.5 text-xs border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-1">
                      <Clock className="h-3 w-3" />
                      Keine Marktvalidierung
                    </Badge>
                  )}
                  {option.created_by_name && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      {option.created_by_name}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold tracking-tight leading-snug pr-8">
                  {option.name}
                </h3>

                {/* Ein-Satz-Wahrheit - immer sichtbar */}
                <p className={cn(
                  "text-sm font-medium italic",
                  isScaling ? "text-amber-700 dark:text-amber-400" : "text-primary"
                )}>
                  „{truth}"
                </p>

                {/* Quick Bullets - Always visible */}
                <div className="space-y-2">
                  {quickBullets.map((bullet, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      {bullet.type === 'goal' && (
                        <span className={cn("mt-1.5 h-1.5 w-1.5 rounded-full shrink-0", config.dotColor)} />
                      )}
                      {bullet.type === 'plus' && (
                        <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                      )}
                      {bullet.type === 'minus' && (
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                      )}
                      <span className="text-muted-foreground leading-snug">{bullet.text}</span>
                    </div>
                  ))}

                  {/* Additional bullets - expandable */}
                  {showMoreBullets && (
                    <div className="space-y-2 pt-1 animate-in fade-in slide-in-from-top-2 duration-200">
                      {additionalAdvantages.map((adv, i) => (
                        <div key={`adv-${i}`} className="flex items-start gap-2 text-sm">
                          <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                          <span className="text-muted-foreground leading-snug">{adv}</span>
                        </div>
                      ))}
                      {additionalRisks.map((risk, i) => (
                        <div key={`risk-${i}`} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                          <span className="text-muted-foreground leading-snug">{risk}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show more button */}
                  {hasMoreBullets && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-xs text-primary hover:text-primary/80 hover:bg-transparent font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMoreBullets(!showMoreBullets);
                      }}
                    >
                      <ChevronRight className={cn(
                        "h-3 w-3 mr-1 transition-transform duration-200",
                        showMoreBullets && "rotate-90"
                      )} />
                      {showMoreBullets 
                        ? "Weniger anzeigen" 
                        : `+${additionalAdvantages.length + additionalRisks.length} weitere`}
                    </Button>
                  )}
                </div>

                {/* Economic Metrics - Always Visible */}
                {hasEconomicMetrics && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                    {option.estimated_cost_min != null && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <DollarSign className="h-3 w-3 shrink-0" />
                        <span className="font-medium text-foreground">{formatEur(option.estimated_cost_min, option.estimated_cost_max)}</span>
                      </div>
                    )}
                    {option.time_to_first_signal && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Timer className="h-3 w-3 shrink-0" />
                        <span className={cn("font-medium", isConstraintBreak ? "text-destructive" : "text-foreground")}>{option.time_to_first_signal}</span>
                      </div>
                    )}
                    {option.capacity_requirement_percent != null && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Gauge className="h-3 w-3 shrink-0" />
                        <span className={cn("font-medium",
                          totalCapacity != null && totalCapacity > 80 ? "text-destructive" : "text-foreground"
                        )}>
                          {option.capacity_requirement_percent}% Kapa
                          {totalCapacity != null && (
                            <span className="text-muted-foreground font-normal"> (ges: {totalCapacity}%)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {option.reversibility_level && reversibilityBadge(option.reversibility_level) && (
                      <div className="flex items-center gap-1.5 text-xs">
                        <RotateCcw className="h-3 w-3 text-muted-foreground shrink-0" />
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", reversibilityBadge(option.reversibility_level)!.className)}>
                          {reversibilityBadge(option.reversibility_level)!.label}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                {/* Effective Exposure - if available */}
                {effectiveExposure != null && effectiveExposure > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                    <Flame className="h-3 w-3 shrink-0" />
                    <span className={cn("font-medium",
                      effectiveExposure < Number(dailyExposure) ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
                    )}>
                      Burn/Tag (eff.): {effectiveExposure.toLocaleString("de-DE")} €
                    </span>
                    <span className="text-[10px] text-muted-foreground/60">(modelliert)</span>
                  </div>
                )}
              </div>

              {/* Expand indicator */}
              <div className="shrink-0 mt-1">
                <ChevronDown 
                  className={cn(
                    "h-5 w-5 text-muted-foreground transition-transform duration-200",
                    isOpen && "rotate-180"
                  )} 
                />
              </div>
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className="animate-accordion-down">
          <CardContent className="pt-0 pb-5 px-5 space-y-4 border-t border-border/50 bg-gradient-to-b from-primary/[0.02] to-transparent">
            {/* Constraint Break Warning */}
            {isConstraintBreak && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mt-4">
                <ShieldAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">
                    Diese Option liefert kein Marktsignal innerhalb eurer Validierungsdeadline.
                  </p>
                  {option.time_to_first_signal_days != null && (
                    <p className="text-sm text-destructive/80 mt-1">
                      Signal nach ca. {option.time_to_first_signal_days} Tagen – Deadline liegt bei {sessionMeta?.phase1_3_irreversibility?.validation_deadline_days || "?"} Tagen.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Strategy Shift Warning */}
            {isStrategyShift && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mt-4">
                <GitBranch className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">
                    Diese Option bricht euer Phase-1-Commitment.
                  </p>
                  <p className="text-sm text-destructive/80 mt-1">
                    Sie verändert eure strategische Wette und adressiert ein anderes Marktsegment.
                  </p>
                </div>
              </div>
            )}

            {/* Scaling-Warnung im Detail */}
            {isScaling && !isConstraintBreak && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mt-4">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">
                    Diese Option ist keine Strategie – sie ist ein Aufschub.
                  </p>
                  <p className="text-sm text-destructive/80 mt-1">
                    Du gewinnst Zeit, ohne dich festzulegen – auf Kosten realer Marktrückmeldung.
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            <p className={cn("text-sm text-muted-foreground", !isScaling && !hasConstraintWarning && "pt-4")}>
              {option.description}
            </p>

            {/* Context Reference */}
            {option.user_context_reference && (
              <div className="rounded-lg p-3 bg-primary/5 border border-primary/10">
                <div className="flex items-start gap-2">
                  <Quote className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-primary mb-1">Bezug zu deiner Kernhypothese</p>
                    <p className="text-sm text-foreground/90">{option.user_context_reference}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Full Advantages & Risks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                  Was du gewinnst
                </p>
                <ul className="space-y-1.5">
                  {option.key_advantages.map((adv, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{adv}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-destructive uppercase tracking-wide">
                  Was du aufgibst
                </p>
                <ul className="space-y-1.5">
                  {option.key_risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Strategic Fit - Kill Criterion & Inaction Cost */}
            {(option.kill_criterion_impact || option.inaction_cost_effect) && (
              <div className="pt-3 border-t border-border/50 space-y-2">
                <p className="text-xs font-medium text-primary uppercase tracking-wide">Strategischer Fit</p>
                {option.kill_criterion_impact && (
                  <div className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <div>
                      <span className="text-xs text-muted-foreground">Kipppunkt-Bezug: </span>
                      <span className="text-foreground/90">{option.kill_criterion_impact}</span>
                    </div>
                  </div>
                )}
                {option.inaction_cost_effect && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-xs text-muted-foreground">Inaction-Kosten:</span>
                    <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", 
                      option.inaction_cost_effect === "eliminates" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" :
                      option.inaction_cost_effect === "reduces" ? "border-border bg-muted/50 text-muted-foreground" :
                      option.inaction_cost_effect === "shifts" ? "border-border bg-muted/50 text-muted-foreground" :
                      "border-destructive/30 bg-destructive/10 text-destructive"
                    )}>
                      {option.inaction_cost_effect === "eliminates" ? "Eliminiert" :
                       option.inaction_cost_effect === "reduces" ? "Reduziert" :
                       option.inaction_cost_effect === "shifts" ? "Verschiebt" : "Ignoriert"}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Alignment Score Details */}
            {alignment.deductions.length > 0 && (
              <div className="pt-3 border-t border-border/50">
                <p className="text-xs font-medium text-muted-foreground mb-1">Alignment-Abzüge</p>
                <div className="flex flex-wrap gap-1.5">
                  {alignment.deductions.map((d, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-border bg-muted/50 text-muted-foreground">
                      {d}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Differentiation */}
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs font-medium text-primary mb-1">Der echte Preis</p>
              <p className="text-sm text-foreground/80">{option.differentiation_factor}</p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
